import { useState, useCallback } from 'react';

interface ProdutoVendasDrillDownData {
  produto: {
    cod_item: string;
    descr_compl: string;
    valor_total_vendas: number;
    total_debito_icms: number;
    total_transacoes: number;
    quantidade_total: number;
    valor_medio_transacao: number;
    preco_medio_unitario: number;
    total_documentos: number;
    participacao_percentual: number;
  };
  vendas_por_mes: Array<{
    mes: number;
    ano: number;
    valor_vendas: number;
    debito_icms: number;
    total_transacoes: number;
    quantidade_vendida: number;
    ticket_medio: number;
  }>;
  top_cfops: Array<{
    cfop: string;
    valor_total: number;
    total_transacoes: number;
    quantidade_total: number;
    valor_medio: number;
  }>;
  top_clientes: Array<{
    cod_participante: string;
    nome: string;
    uf: string;
    municipio: string;
    total_vendas: number;
    icms_vendas: number;
    quantidade_vendida: number;
    total_documentos: number;
    total_operacoes: number;
    ticket_medio: number;
    preco_medio_unitario: number;
  }>;
  top_regioes: Array<{
    uf: string;
    valor_total: number;
    total_transacoes: number;
    quantidade_total: number;
    total_clientes: number;
    ticket_medio: number;
  }>;
  transacoes_detalhadas: Array<{
    dt_emissao: string;
    num_doc: string;
    cfop: string;
    vl_item: number;
    vl_icms: number;
    qtd: number;
    cliente: string;
    preco_unitario: number;
  }>;
}

interface UseProdutoVendasDrillDownProps {
  spedFiscalId?: number | null;
}

interface UseProdutoVendasDrillDownReturn {
  isModalOpen: boolean;
  drillDownData: ProdutoVendasDrillDownData | null;
  loading: boolean;
  error: string | null;
  selectedProduto: string | null;
  openModal: (produtoName: string, codItem: string, spedFiscalId: number) => Promise<void>;
  closeModal: () => void;
  retry: () => void;
}

export const useProdutoVendasDrillDown = ({ spedFiscalId }: UseProdutoVendasDrillDownProps): UseProdutoVendasDrillDownReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [drillDownData, setDrillDownData] = useState<ProdutoVendasDrillDownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduto, setSelectedProduto] = useState<string | null>(null);
  const [currentCodItem, setCurrentCodItem] = useState<string | null>(null);
  const [currentSpedFiscalId, setCurrentSpedFiscalId] = useState<number | null>(null);

  const fetchDrillDownData = useCallback(async (codItem: string, spedFiscalId: number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}sped-fiscal/produto-vendas-drill-down/${spedFiscalId}/${encodeURIComponent(codItem)}`
      );

      if (!response.ok) {
        throw new Error(`Erro ao buscar dados: ${response.status}`);
      }

      const data: ProdutoVendasDrillDownData = await response.json();
      
      // Garantir que top_regioes seja sempre um array válido
      if (data.top_regioes && !Array.isArray(data.top_regioes)) {
        data.top_regioes = [];
      }
      
      setDrillDownData(data);
    } catch (err) {
      console.error('Erro ao buscar drill down do produto:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, []);

  const openModal = useCallback(async (produtoName: string, codItem: string, spedFiscalId: number) => {
    setSelectedProduto(produtoName);
    setCurrentCodItem(codItem);
    setCurrentSpedFiscalId(spedFiscalId);
    setIsModalOpen(true);
    setLoading(true);
    setError(null);
    
    await fetchDrillDownData(codItem, spedFiscalId);
  }, [fetchDrillDownData]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProduto(null);
    setCurrentCodItem(null);
    setCurrentSpedFiscalId(null);
    setDrillDownData(null);
    setError(null);
  }, []);

  const retry = useCallback(() => {
    if (currentCodItem && currentSpedFiscalId) {
      fetchDrillDownData(currentCodItem, currentSpedFiscalId);
    }
  }, [currentCodItem, currentSpedFiscalId, fetchDrillDownData]);

  return {
    isModalOpen,
    drillDownData,
    loading,
    error,
    selectedProduto,
    openModal,
    closeModal,
    retry
  };
}; 